import { useEffect } from 'react';
import {
  Modal,
  TextInput,
  Select,
  Button,
  Stack,
  Group,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { FormField } from '@/components/ui/FormField';
import { useOrganization } from '@/hooks/useOrganization';
import { useCreateAuditEvent, useUpdateAuditEvent } from '@/hooks/api/useAudits';
import { notifications } from '@mantine/notifications';
import type { AssetAuditEvent } from '@/types/models';

interface AuditFormProps {
  opened: boolean;
  onClose: () => void;
  audit?: AssetAuditEvent | null;
}

export function AuditForm({ opened, onClose, audit }: AuditFormProps) {
  const { organization, organizationId } = useOrganization();
  const createAuditEvent = useCreateAuditEvent();
  const updateAuditEvent = useUpdateAuditEvent();

  const form = useForm({
    initialValues: {
      name: '',
      status: 'draft' as const,
      audit_date: null as Date | null,
      due_by_date: null as Date | null,
    },
    validate: {
      name: (value) => (value ? null : 'Name is required'),
    },
  });

  useEffect(() => {
    if (audit) {
      form.setValues({
        name: audit.name,
        status: audit.status,
        audit_date: audit.audit_date ? new Date(audit.audit_date) : null,
        due_by_date: audit.due_by_date ? new Date(audit.due_by_date) : null,
      });
    } else {
      form.reset();
    }
  }, [audit]);

  const handleSubmit = async (values: typeof form.values) => {
    if (!organizationId) return;

    try {
      const data = {
        organization_id: organizationId,
        name: values.name,
        status: values.status,
        audit_date: values.audit_date?.toISOString().split('T')[0] || null,
        due_by_date: values.due_by_date?.toISOString().split('T')[0] || null,
        auditor_id: null, // TODO: Add auditor selection
      };

      if (audit) {
        await updateAuditEvent.mutateAsync({
          id: audit.id,
          ...data,
        });
        notifications.show({
          title: 'Audit Updated',
          message: 'The audit has been updated successfully',
          color: 'green',
        });
      } else {
        await createAuditEvent.mutateAsync(data);
        notifications.show({
          title: 'Audit Created',
          message: 'The audit has been created successfully',
          color: 'green',
        });
      }

      form.reset();
      onClose();
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: audit ? 'Failed to update audit' : 'Failed to create audit',
        color: 'red',
      });
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={audit ? 'Edit Audit' : 'Create New Audit'}
      size="lg"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <FormField label="Audit Name" required error={form.errors.name}>
            <TextInput
              placeholder="e.g., Q1 2024 Asset Verification"
              {...form.getInputProps('name')}
              error={undefined}
            />
          </FormField>

          <FormField label="Status" required error={form.errors.status}>
            <Select
              data={[
                { value: 'draft', label: 'Draft' },
                { value: 'in_progress', label: 'In Progress' },
                { value: 'completed', label: 'Completed' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
              {...form.getInputProps('status')}
              error={undefined}
            />
          </FormField>

          <FormField label="Audit Date" error={form.errors.audit_date}>
            <DateInput
              placeholder="Select audit date"
              valueFormat="YYYY-MM-DD"
              {...form.getInputProps('audit_date')}
              error={undefined}
            />
          </FormField>

          <FormField label="Due By Date" error={form.errors.due_by_date}>
            <DateInput
              placeholder="Select due date"
              valueFormat="YYYY-MM-DD"
              {...form.getInputProps('due_by_date')}
              error={undefined}
            />
          </FormField>

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createAuditEvent.isPending || updateAuditEvent.isPending}
            >
              {audit ? 'Update Audit' : 'Create Audit'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
